'use es6';

import { useSelector } from 'react-redux';
import { TRAFFIC_ANALYTICS_API_ACCESS } from 'dashboard-lib/public/scope/scope-name';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ErrorMessage from 'reporting-ui-components/components/shared/ErrorMessage';
import SnowflakeReportError from 'reporting-snowflake/relational/components/SnowflakeReportError';
import * as errorTypes from 'reporting-ui-components/reports/decorators/DataDecorator/errors';
import isEditableInBuilder from 'reporting-ui-components/lib/isEditableInBuilder';
import UIPricingPageRedirectButton from 'ui-addon-upgrades/button/UIPricingPageRedirectButton';
import { REPORT_DATA_ACCESS_DENIED } from 'dashboard-lib/public/sentry/sentry-event-id';
import { capture } from 'dashboard-lib/public/sentry/report-management-sentry';
import { getId } from 'dashboard-lib/public/report/report-schema';
import { isV2 } from 'dashboard-lib/public/report/report-logic';
import { selectIsSuperAdmin, selectIsSuperUser } from 'dashboard-lib/public/userInfo/scopes';
import ViewReportErrorWrapper from 'dashboard-components/error/ViewReportErrorWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const reBasicError = /This.*does not have proper permissions.*\[(.*)\]/;
const reAnalyticsError = /Insufficient scopes, requires.*\[(.*)\]/;
const reFrameworkDataError = /User .* does not have all required scopes (.*) to access .*/;
const parseMissingScopes = message => {
  let scopeString;
  if (reBasicError.test(message)) {
    scopeString = reBasicError.exec(message)[1];
  } else if (reAnalyticsError.test(message)) {
    scopeString = reAnalyticsError.exec(message)[1];
  } else if (reFrameworkDataError.test(message)) {
    scopeString = reFrameworkDataError.exec(message)[1];
  }
  if (scopeString) {
    return scopeString.split(',').map(scope => scope.trim());
  }
  return [];
};
const TrafficAnalyticsAccessDenied = () => /*#__PURE__*/_jsxs(ViewReportErrorWrapper, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "reports.errors.missingTrafficAnalyticsPermissions.title"
  }),
  children: [/*#__PURE__*/_jsx("p", {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reports.errors.missingTrafficAnalyticsPermissions.message"
    })
  }), /*#__PURE__*/_jsx(UIPricingPageRedirectButton, {
    upgradeData: {
      app: 'reporting',
      screen: 'dashboard',
      upgradeProduct: 'marketing-pro',
      uniqueId: 'traffic-drilldown'
    }
  })]
});
const GeneralAccessDenied = () => {
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const isSuperUser = useSelector(selectIsSuperUser);
  return /*#__PURE__*/_jsx(ViewReportErrorWrapper, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reports.errors.dataTypeAccessDenied.title"
    }),
    children: isSuperAdmin || isSuperUser ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reports.errors.dataTypeAccessDenied.portalMessage"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reports.errors.dataTypeAccessDenied.userMessage"
    })
  });
};
const getCustomErrorState = (error, report) => {
  const {
    status,
    responseJSON
  } = error;
  const message = responseJSON ? responseJSON.message : '';
  const missingScopes = parseMissingScopes(message);
  if (status === 403) {
    if (missingScopes.includes(TRAFFIC_ANALYTICS_API_ACCESS)) {
      return /*#__PURE__*/_jsx(TrafficAnalyticsAccessDenied, {});
    }
    if (missingScopes.length > 0) {
      capture(REPORT_DATA_ACCESS_DENIED, {
        error,
        extra: {
          reportId: getId(report)
        },
        fingerprint: [REPORT_DATA_ACCESS_DENIED]
      });
      return /*#__PURE__*/_jsx(GeneralAccessDenied, {});
    }
    return null;
  }
  return null;
};
const ReportErrorMessage = (error = {}, report) => {
  const customErrorState = getCustomErrorState(error, report);
  return customErrorState || (isV2(report) ? /*#__PURE__*/_jsx(SnowflakeReportError, {
    error: error
  }) : /*#__PURE__*/_jsx(ErrorMessage, {
    type: errorTypes.get(error),
    error: error,
    editableReport: isEditableInBuilder(report) && report
  }));
};
export default ReportErrorMessage;